import { Box, Paper, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';

import { DescriptionType, InfoLanguage } from "@bandido/shared/enums";

import { useGetHotelNimbuxHotelById, useGetPropertyFares } from "@frontend/features/hotel-content-manager/queries";
import { HotelAmenities, HotelBasicInfo, HotelDescription, HotelImageGallery, ImageModal } from "@frontend/features/hotel-content/components/Detail/HotelView";
import { hotelViewStyles } from "@frontend/features/hotel-content/components/Detail/HotelView/styles";
import { useHotelImagesModal } from "@frontend/features/hotel-content/hooks";
import { HotelPropertyFaresFilters } from "@frontend/features/hotel/components/HotelPropertyFaresFilters";
import { HotelPropertyFaresList } from "@frontend/features/hotel/components/HotelPropertyFaresList";
import { HotelSearchPrimaryColor } from "@frontend/features/hotel/components/HotelSearchPrimaryColor";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { CONFIG } from "@frontend/config";
import { QueryKeys } from "@frontend/features/common/types";
import { useQueryClient } from "react-query";

const LoadingState = () => (
  <Box sx={hotelViewStyles.container}>
    <Box display="flex" flexDirection="row" gap={1}>
      <Skeleton variant="rounded" width={716} height={600} />
      <Box display="flex" flexDirection="column" gap={1}>
        <Skeleton variant="rounded" width={358} height={195} />
        <Skeleton variant="rounded" width={358} height={195} />
        <Skeleton variant="rounded" width={358} height={195} />
      </Box>
    </Box>
    <Box display="flex" flexDirection="column" gap={1}>
      <Skeleton variant="rounded" width={270} height={32} />
      <Skeleton variant="rounded" width={200} height={22} />
    </Box>
    <Box display="flex" flexDirection="column" mt={2} gap={1}>
      <Skeleton variant="rounded" width={180} height={32} />
    </Box>
    <Box display="flex" flexDirection="column" mt={2} gap={1}>
      <Skeleton variant="rounded" width={900} height={15} />
      <Skeleton variant="rounded" width={900} height={15} />
      <Skeleton variant="rounded" width={850} height={15} />
      <Skeleton variant="rounded" width={900} height={15} />
      <Skeleton variant="rounded" width={900} height={15} />
      <Skeleton variant="rounded" width={850} height={15} />
      <Skeleton variant="rounded" width={700} height={15} />
    </Box>
  </Box>
);

const socketUrl = `${CONFIG.WS_URL}/hotel-fares`;
export const HotelPropertyFares = () => {
  const roomIdRef = useRef(uuidv4());
  const socketRef = useRef<Socket>();
  const queryClient = useQueryClient();
  const { hotelId } = useParams<{ hotelId: string }>();
  const { data: nimbuxHotel, isLoading } = useGetHotelNimbuxHotelById(Number(hotelId));
  const { i18n } = useTranslation();

  const images = nimbuxHotel?.medias ?? [];
  const imageModal = useHotelImagesModal(images);

  const { search } = useLocation();

  const params = new URLSearchParams(search);
  const checkIn = params.get('checkin');
  const checkOut = params.get('checkout');
  const occupancies = params.get('rooms')?.split('|').map(room => ({ paxes: room.split(',').map(age => ({ age: Number(age) })) }));

  const requestPayload = useMemo(() => ({
    checkIn,
    checkOut,
    occupancies,
    nimbuxPropertyIds: [Number(hotelId)],
    socketRef: roomIdRef.current,
  }), [checkIn, checkOut, occupancies, hotelId, roomIdRef.current]);


useEffect(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    socketRef.current = io(socketUrl, {
      transports: ['websocket'],
      path: '/v1/api/socket.io',
      query: { agencyId: CONFIG.AGENCY_ID, roomId: roomIdRef.current },
    });
    socketRef.current?.on('connect', () => {
      console.log('WebSocket connected');
    });

    socketRef.current?.on('disconnect', () => {
      console.log('WebSocket disconnected');
    });

    socketRef.current?.on('connect_error', (err: Error) => {
      console.error('WebSocket error', err);
    });

    socketRef.current?.on('fares_emitted', (data: any) => {
      console.log(data);
      invalidatePropertyFares();
    });

    return () => {
      socketRef.current?.off('fares_emitted');
      socketRef.current?.disconnect();
    };
  }, [
    socketUrl, requestPayload
  ]);

  const { data: propertyFares, isLoading: isLoadingFares } = useGetPropertyFares(requestPayload);

  const invalidatePropertyFares = () => {
    queryClient.invalidateQueries([QueryKeys.getHotelPropertyFares, requestPayload]);
  }

  if (isLoading) return <LoadingState />;

  const data = {
    rank: 5,
  };

  const hotelDescription = nimbuxHotel?.descriptions
    ?.find((description) => description.type === DescriptionType.GENERAL)
    ?.texts[i18n.language as InfoLanguage];

  return (
    <>
      <HotelSearchPrimaryColor />
      <Box display="flex" flexDirection="column" gap={3} maxWidth={1082} mx="auto" mt={3} mb={3}>
        <Paper elevation={1}>
          <Box sx={hotelViewStyles.container}>
            {images.length > 0 && (
              <HotelImageGallery
                images={images}
                onImageClick={imageModal.handleImageClick}
              />
            )}

            {nimbuxHotel?.name && (
              <HotelBasicInfo
                name={nimbuxHotel.name}
                rating={data.rank}
                impressions={nimbuxHotel.impressions}
              />
            )}

            <HotelDescription description={hotelDescription} />

            <HotelAmenities amenities={nimbuxHotel?.amenities || []} />

            <ImageModal
              open={imageModal.modalOpen}
              currentIndex={imageModal.currentImageIndex}
              images={images}
              onClose={imageModal.handleCloseModal}
              onNext={imageModal.handleNextImage}
              onPrev={imageModal.handlePrevImage}
            />
          </Box>
        </Paper>

        <HotelPropertyFaresFilters />

        <HotelPropertyFaresList loading={isLoadingFares} fares={propertyFares} />
      </Box>
    </>
  );
};
